import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { getFieldNotes, getFrameworks, getEvents } from "@/lib/api";
import { Search as SearchIcon, ArrowRight } from "lucide-react";

// Lightweight on-site search across the indexable content: Field Notes,
// Frameworks, and Events. Backs the WebSite SearchAction (sitelinks search box).
export default function Search() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [notes, setNotes] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [n, f, e] = await Promise.all([
          getFieldNotes().catch(() => []),
          getFrameworks().catch(() => []),
          getEvents().catch(() => []),
        ]);
        if (!active) return;
        setNotes(Array.isArray(n) ? n : n?.items || []);
        setFrameworks(Array.isArray(f) ? f : f?.items || []);
        setEvents(Array.isArray(e) ? e : e?.items || []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const term = q.trim().toLowerCase();

  const results = useMemo(() => {
    const match = (s) => (s || "").toLowerCase().includes(term);
    if (!term) return { notes: [], frameworks: [], events: [], total: 0 };
    const rn = notes.filter((x) => match(x.title) || match(x.excerpt) || match((x.tags || []).join(" ")));
    const rf = frameworks.filter((x) => match(x.title) || match(x.description) || match(x.tag));
    const re = events.filter((x) => match(x.title) || match(x.description) || match(x.location));
    return { notes: rn, frameworks: rf, events: re, total: rn.length + rf.length + re.length };
  }, [term, notes, frameworks, events]);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams(q.trim() ? { q: q.trim() } : {});
  };

  return (
    <div className="exec-scope min-h-screen" data-testid="search-page">
      <Seo
        title="Search — Copper & Vine"
        description="Search Copper & Vine field notes, frameworks, and events."
        path="/search"
        noindex
      />
      <section className="exec-container pt-24 md:pt-28 pb-24">
        <div className="max-w-[820px]">
          <p className="exec-eyebrow">Search</p>
          <h1 className="font-extralight text-[clamp(34px,4.8vw,58px)] leading-[1.1] mt-5">
            Search the library.
          </h1>
          <form onSubmit={onSubmit} className="relative mt-9" role="search">
            <SearchIcon size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-[color:var(--grey)]" />
            <input
              data-testid="search-input"
              autoFocus
              className="exec-underline pl-8"
              placeholder="Search field notes, frameworks, events"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </form>

          <div className="mt-10">
            {!term ? (
              <p className="exec-serif text-[17px] text-[color:var(--lead-ink)]">Type to search the on-site library.</p>
            ) : loading ? (
              <p className="exec-serif text-[17px] text-[color:var(--grey)]">Searching…</p>
            ) : results.total === 0 ? (
              <p data-testid="search-empty" className="exec-serif text-[17px] text-[color:var(--grey)]">
                No results for “{q}”. Try the <Link to="/resources" className="exec-link inline">Resources</Link> hub.
              </p>
            ) : (
              <div className="space-y-10" data-testid="search-results">
                {results.notes.length > 0 && (
                  <ResultGroup label="Field Notes" count={results.notes.length}>
                    {results.notes.map((x) => (
                      <ResultRow key={x.slug} to={`/resources/field-notes/${x.slug}`} title={x.title} sub={x.excerpt} />
                    ))}
                  </ResultGroup>
                )}
                {results.frameworks.length > 0 && (
                  <ResultGroup label="Frameworks" count={results.frameworks.length}>
                    {results.frameworks.map((x) => (
                      <ResultRow key={x.id || x.title} to="/resources" title={x.title} sub={x.description} />
                    ))}
                  </ResultGroup>
                )}
                {results.events.length > 0 && (
                  <ResultGroup label="Events" count={results.events.length}>
                    {results.events.map((x) => (
                      <ResultRow key={x.slug} to={`/events/${x.slug}`} title={x.title} sub={x.location} />
                    ))}
                  </ResultGroup>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ResultGroup({ label, count, children }) {
  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <p className="exec-eyebrow whitespace-nowrap">{label}</p>
        <span className="h-px flex-1 bg-[color:var(--rule)]" aria-hidden="true" />
        <span className="text-[11px] tracking-[0.14em] text-[color:var(--grey)]">{count}</span>
      </div>
      <div className="divide-y divide-[color:var(--rule)]">{children}</div>
    </div>
  );
}

function ResultRow({ to, title, sub }) {
  return (
    <Link to={to} data-testid="search-result-row" className="group block py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-light text-[18px] text-[color:var(--ink)]">{title}</p>
          {sub && <p className="exec-serif text-[15.5px] text-[color:var(--lead-ink)] mt-1 line-clamp-2">{sub}</p>}
        </div>
        <ArrowRight size={16} className="text-[color:var(--grey)] group-hover:text-[color:var(--copper)] transition-colors shrink-0 mt-1" />
      </div>
    </Link>
  );
}
